import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { Check, ChevronsUpDown } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AdminClient } from '~/api/adminClient'
import { Badge } from '~/components/ui/badge'
import { Button } from '~/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandInput,
  CommandItem,
  CommandList,
} from '~/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '~/components/ui/popover'
import { useActiveTenant } from './activeTenant'
import { tenantOrigin } from './tenantHost'

/**
 * Switches tenant by navigating to its host. Hidden for users with a single
 * tenant. Staff see every tenant (the backend does not scope them).
 */
export function TenantSwitcher({
  client,
  baseDomain,
}: {
  client: AdminClient
  baseDomain: string
}) {
  const { t } = useTranslation()
  const active = useActiveTenant()
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')
  const { data } = useQuery({
    queryKey: ['tenantSwitcher', search],
    queryFn: () =>
      client.tenants.list({
        page: 1,
        pageSize: 20,
        sort: [{ field: 'name', order: 'asc' }],
        filters:
          search === ''
            ? undefined
            : [{ field: 'name', operator: 'contains', value: search }],
      }),
    // keep the list (and the open popover) while the next search loads
    placeholderData: keepPreviousData,
  })

  if (data === undefined || (search === '' && data.total <= 1)) {
    return <span className="font-medium">{active.name}</span>
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
    >
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label={t('shell.tenantSwitcher.label')}
          className="gap-2"
        >
          {active.name}
          {active.status === 'suspended' && (
            <Badge variant="secondary">
              {t('shell.tenantSwitcher.suspended')}
            </Badge>
          )}
          <ChevronsUpDown className="size-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-72 p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder={t('shell.tenantSwitcher.search')}
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>{t('shell.tenantSwitcher.empty')}</CommandEmpty>
            {data.items.map((tenant) => (
              <CommandItem
                key={tenant.id}
                value={tenant.id}
                onSelect={() => {
                  window.location.assign(
                    tenantOrigin(tenant.slug, baseDomain, window.location),
                  )
                }}
              >
                {tenant.name}
                {tenant.id === active.id && (
                  <Check className="ml-auto size-4" />
                )}
              </CommandItem>
            ))}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  )
}
